import {
	FormControl,
	FormHelperText,
	Grid2,
	TextField,
	Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

import { FooterDetailsButtons } from "@/controls/buttons/FooterDetailsButtons";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { ErrorModal, getMomentTz, TimeService } from "@dexteel/mesf-core";
import { DateTimePicker } from "@mui/x-date-pickers";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Moment } from "moment";
import { SectionSelector } from "@/shared/selectors";
import { MechanicalResultsTable } from "@/pages/mill/schedule/components/mechanical-results/MechanicalResultsTable";
import { useSaveMechanicalRun } from "@/pages/mill/schedule/components/mechanical-results/useSaveMechanicalRun";
import {
	Campaign,
	CAMPAIGN_INITIAL_VALUES,
} from "@/pages/mill/schedule/models/Campaign";
import { MILL_1 } from "@/setup/mill-config/mill-config";
import { upsertM1Campaign } from "../repositories/M1CampaignRepository";
import {
	useHeatMechanicalResult,
	useHeatTestDisposition,
} from "@/pages/lab/chemistries/components/MechanicalResultsModal";

const moment = getMomentTz();

type Props = {
	campaign: Campaign | null;
	modal: "create" | "update" | "delete" | "";
	onHide: ({
		shouldUpdate,
		close,
		entityId,
	}: {
		shouldUpdate: boolean;
		close: boolean;
		entityId?: number;
	}) => void;
};

export const M1CampaignDetails = ({ campaign, modal, onHide }: Props) => {
	const [message, setNotificationMessage] = useState<string>("");
	const [error, setError] = useState<string>("");
	const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false);

	const succesfullyMessage = `The scheduled section was ${modal}d succesfully`;

	const {
		control,
		reset,
		handleSubmit,
		setValue,
		watch,
		formState: { dirtyFields },
	} = useForm<Campaign>({
		mode: "onChange",
	});

	const isFormDirty =
		!(Object.keys(dirtyFields).length === 0) || modal === "create";

	const queryClient = useQueryClient();
	const upsertMutation = useMutation(upsertM1Campaign, {
		onSuccess: async (id: number) => {
			setNotificationMessage(succesfullyMessage);
			await queryClient.invalidateQueries(["m1Campaigns"]);
			onHide({ shouldUpdate: true, close: false, entityId: id });
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => setIsSubmitLoading(false),
	});

	const {
		data: dispositionsHistory,
		error: e,
		isError,
	} = useHeatTestDisposition({
		heatId: campaign?.HeatId!,
		millId: MILL_1.id,
	});

	const {
		data: mechanicalData,
		error: mechanicalResultsError,
		isError: isMechanicalResultsError,
	} = useHeatMechanicalResult({
		heatId: campaign?.HeatId!,
	});

	const mechanicalResults = mechanicalData?.results;

	const onSubmit: SubmitHandler<Campaign> = async (data) => {
		setIsSubmitLoading(true);
		if (modal !== "delete") {
			if (campaign?.IsDuplicate) {
				upsertMutation.mutate({ ...data, CampaignId: null });
			} else {
				upsertMutation.mutate(data);
			}
		}
	};

	const validateCampaignDuration = (
		startDate: Moment | null,
		endDate: Moment | null,
	) => {
		if (!startDate || !endDate) {
			return "Start date and end date are required.";
		}
		const differenceInDays = endDate.diff(startDate, "days");
		if (differenceInDays > 30) {
			return "The duration of the campaign cannot exceed 30 days.";
		}
		return true;
	};

	useEffect(() => {
		if (modal !== "") {
			reset(campaign || CAMPAIGN_INITIAL_VALUES);
			const startDate = campaign?.Start
				? new Date(campaign?.Start)
				: new Date();
			setValue("Start", startDate);
			setIsSubmitLoading(false);
		}
	}, [modal, campaign]);

	useEffect(() => {
		if (
			(e && isError) ||
			(mechanicalResultsError && isMechanicalResultsError)
		) {
			setError(e?.message || mechanicalResultsError?.message || "Error");
		}
	}, [e, isError, mechanicalResultsError, isMechanicalResultsError]);

	return (
		<>
			{campaign === null && modal !== "create" ? (
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						height: "100%",
						width: "100%",
					}}
				>
					<Typography>Select a campaign</Typography>
				</div>
			) : (
				<form onSubmit={handleSubmit(onSubmit)}>
					<Grid2 container spacing={2} p={2} alignItems="center">
						<Grid2 size={{ xs: 12, md: 3 }}>
							<Controller
								name="CampaignNumber"
								rules={{ required: "The number can't be null" }}
								control={control}
								render={({
									field: { onChange, value },
									fieldState: { error },
								}) => (
									<TextField
										required
										label="Number"
										variant="outlined"
										fullWidth
										size="small"
										autoComplete="off"
										value={value ?? ""}
										onChange={onChange}
										error={!!error}
										helperText={error?.message}
										disabled
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ sm: 12, md: 3 }} alignItems="flex-start">
							<Controller
								name="SectionId"
								rules={{ required: "The section can't be null" }}
								control={control}
								render={({ field, fieldState: { error } }) => (
									<SectionSelector
										label="Section"
										value={field.value}
										onChange={field.onChange}
										disabled
										fieldError={error}
										required
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ sm: 12, md: 3 }}>
							<Controller
								name="HeatName"
								control={control}
								render={({ field }) => (
									<TextField
										required
										label="Heat"
										variant="outlined"
										fullWidth
										size="small"
										autoComplete="off"
										value={field.value ?? ""}
										disabled
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ sm: 12, md: 3 }}>
							<Controller
								name="GradeName"
								control={control}
								render={({ field }) => (
									<TextField
										required
										label="Grade"
										variant="outlined"
										fullWidth
										size="small"
										autoComplete="off"
										value={field.value ?? ""}
										disabled
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Controller
								name="Start"
								control={control}
								shouldUnregister
								rules={{
									deps: ["End"],
									required: "Start date can't be null",
									validate: {
										validateDuration: (value) => {
											const endDate = watch("End");
											return validateCampaignDuration(
												moment(value),
												moment(endDate),
											);
										},
									},
								}}
								render={({ field, fieldState: { error } }) => (
									<FormControl fullWidth error={!!error}>
										<DateTimePicker
											disabled
											label="Start"
											format="MM/DD/YYYY HH:mm:ss"
											views={["year", "month", "day", "hours", "minutes"]}
											ampm={false}
											timezone={TimeService.getInstance().getServerTimeZone()}
											value={field.value ? moment(field.value) : null}
											onChange={field.onChange}
											slotProps={{
												textField: {
													size: "small",
													error: !!error,
												},
											}}
										/>
										{error && <FormHelperText>{error.message}</FormHelperText>}
									</FormControl>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Controller
								name="End"
								control={control}
								shouldUnregister
								rules={{
									deps: ["Start"],
									required: "End date can't be null",
									validate: {
										notBeforeStart: (value) => {
											const startDate = watch("Start");
											if (value && startDate && value < startDate) {
												return "End date cannot be before Start date.";
											}
											return true;
										},
										notSameAsStart: (value) => {
											const startDate = watch("Start");
											if (
												value &&
												startDate &&
												moment(value).isSame(moment(startDate))
											) {
												return "End date cannot be the same as Start date.";
											}
											return true;
										},
										validateDuration: (value) => {
											const startDate = watch("Start");
											return validateCampaignDuration(
												moment(startDate),
												moment(value),
											);
										},
									},
								}}
								render={({ field, fieldState: { error } }) => (
									<FormControl fullWidth error={!!error}>
										<DateTimePicker
											disabled
											label="End"
											format="MM/DD/YYYY HH:mm:ss"
											views={["year", "month", "day", "hours", "minutes"]}
											ampm={false}
											timezone={TimeService.getInstance().getServerTimeZone()}
											value={field.value ? moment(field.value) : null}
											onChange={field.onChange}
											slotProps={{
												textField: {
													size: "small",
													error: !!error,
												},
											}}
										/>
										{error && <FormHelperText>{error.message}</FormHelperText>}
									</FormControl>
								)}
							/>
						</Grid2>
						{/* Hidden temporarily — not tracked in M1 yet */}
						{/* <Grid2 size={{ xs: 12, md: 6 }}>
							<Controller
								name="ChargedBillets"
								control={control}
								render={({
									field: { onChange, value },
									fieldState: { error },
								}) => (
									<TextField
										label="Charged Billets"
										type="number"
										variant="outlined"
										fullWidth
										size="small"
										autoComplete="off"
										value={value ?? ""}
										onChange={(event) =>
											Number(event.target.value) < 0
												? onChange(null)
												: onChange(Number(event.target.value))
										}
										disabled
										error={!!error}
										helperText={error?.message}
									/>
								)}
							/>
						</Grid2> */}
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Controller
								name="RolledBillets"
								control={control}
								render={({
									field: { onChange, value },
									fieldState: { error },
								}) => (
									<TextField
										label="Rolled Billets"
										type="number"
										variant="outlined"
										fullWidth
										size="small"
										autoComplete="off"
										value={value ?? ""}
										onChange={onChange}
										disabled
										error={!!error}
										helperText={error?.message}
									/>
								)}
							/>
						</Grid2>
						{/* Hidden temporarily — not tracked in M1 yet */}
						{/* <Grid2 size={{ xs: 12, md: 4 }}>
							<Controller
								name="ChargedHotTons"
								control={control}
								render={({
									field: { value },
									fieldState: { error },
								}) => (
									<TextField
										label="Charged Hot Tons"
										variant="outlined"
										fullWidth
										size="small"
										autoComplete="off"
										value={parseFloat(value?.toFixed(3) ?? "0") ?? ""}
										disabled
										error={!!error}
										helperText={error?.message}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 4 }}>
							<Controller
								name="RolledHotTons"
								control={control}
								render={({
									field: { value },
									fieldState: { error },
								}) => (
									<TextField
										label="Rolled Hot Tons"
										type="number"
										variant="outlined"
										fullWidth
										size="small"
										autoComplete="off"
										value={parseFloat(value?.toFixed(3) ?? "0") ?? ""}
										disabled
										error={!!error}
										helperText={error?.message}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 4 }}>
							<Controller
								name="RejectedHotTons"
								control={control}
								render={({
									field: { value },
									fieldState: { error },
								}) => (
									<TextField
										label="Rejected Hot Tons"
										type="number"
										variant="outlined"
										fullWidth
										size="small"
										autoComplete="off"
										value={parseFloat(value?.toFixed(3) ?? "0") ?? ""}
										disabled
										error={!!error}
										helperText={error?.message}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 4 }}>
							<Controller
								name="ChargedColdTons"
								control={control}
								render={({
									field: { value },
									fieldState: { error },
								}) => (
									<TextField
										label="Charged Cold Tons"
										variant="outlined"
										fullWidth
										size="small"
										autoComplete="off"
										value={parseFloat(value?.toFixed(3) ?? "0") ?? ""}
										disabled
										error={!!error}
										helperText={error?.message}
									/>
								)}
							/>
						</Grid2> */}
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Controller
								name="RolledColdTons"
								control={control}
								render={({ field: { value }, fieldState: { error } }) => (
									<TextField
										label="Rolled Cold Tons"
										type="number"
										variant="outlined"
										fullWidth
										size="small"
										autoComplete="off"
										value={parseFloat(value?.toFixed(3) ?? "0") ?? ""}
										disabled
										error={!!error}
										helperText={error?.message}
									/>
								)}
							/>
						</Grid2>
						{/* Hidden temporarily — not tracked in M1 yet */}
						{/* <Grid2 size={{ xs: 12, md: 4 }}>
							<Controller
								name="RejectedColdTons"
								control={control}
								render={({
									field: { value },
									fieldState: { error },
								}) => (
									<TextField
										label="Rejected Cold Tons"
										type="number"
										variant="outlined"
										fullWidth
										size="small"
										autoComplete="off"
										value={parseFloat(value?.toFixed(3) ?? "0") ?? ""}
										disabled
										error={!!error}
										helperText={error?.message}
									/>
								)}
							/>
						</Grid2> */}
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Controller
								name="BilletSize"
								control={control}
								render={({ field: { value }, fieldState: { error } }) => (
									<TextField
										label="Billet Size"
										type="number"
										variant="outlined"
										fullWidth
										size="small"
										autoComplete="off"
										value={value ?? ""}
										disabled
										error={!!error}
										helperText={error?.message}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Controller
								name="Description"
								control={control}
								render={({ field: { value }, fieldState: { error } }) => (
									<TextField
										label="Description"
										variant="outlined"
										fullWidth
										size="small"
										autoComplete="off"
										value={value ?? ""}
										disabled
										error={!!error}
										helperText={error?.message}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Controller
								name="MillYield"
								control={control}
								render={({ field: { value }, fieldState: { error } }) => (
									<TextField
										label="Mill Yield"
										type="number"
										variant="outlined"
										fullWidth
										size="small"
										autoComplete="off"
										value={value ?? ""}
										disabled
										error={!!error}
										helperText={error?.message}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<Controller
								name="FinishedYield"
								control={control}
								render={({ field: { value }, fieldState: { error } }) => (
									<TextField
										label="Finished Yield"
										type="number"
										variant="outlined"
										fullWidth
										size="small"
										autoComplete="off"
										value={value ?? ""}
										disabled
										error={!!error}
										helperText={error?.message}
									/>
								)}
							/>
						</Grid2>
						{campaign?.HeatId &&
							((mechanicalResults?.length ?? 0) > 0 ||
								(dispositionsHistory?.length ?? 0) > 0) && (
								<Grid2 size={{ xs: 12 }}>
									<Typography
										variant="body2"
										color="text.secondary"
										sx={{ mb: 0.5 }}
									>
										Mechanical Test Results — Grade:{" "}
										{campaign?.GradeName ?? "-"}
									</Typography>
									<MechanicalResultsTable
										results={mechanicalResults ?? []}
										requirement={mechanicalData?.requirement ?? null}
										dispositions={dispositionsHistory ?? []}
									/>
								</Grid2>
							)}
						<Grid2 size={{ md: 12 }}>
							<FooterDetailsButtons
								isSubmitLoading={isSubmitLoading}
								disabled={!isFormDirty}
								onHide={() => onHide({ shouldUpdate: false, close: true })}
							/>
						</Grid2>
					</Grid2>
				</form>
			)}
			<NotificationSnackBar
				message={message}
				onHide={() => setNotificationMessage("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
